import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import to from "await-to-js";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Dialog, DialogContent, DialogContentText, DialogActions } from "@mui/material";

import UKToast from "@/common-components-src/components/UKToast/index";;

import CommonButton from "@/app/csr-src/pages/order/components/commonButton";

import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";

import styles from "./index.module.scss";

import { fetchRecordList, cancelAfsRecord } from "@/app/csr-src/apis/afterSales/afterSales.api";
import { IRecordListReq, RecordListResVo, ICancelRecordReq } from "@/app/csr-src/apis/afterSales/afterSales.interface";
// 埋点
import { EXPOSURE_RECORD } from "@/tracks/25344";

interface afsRecordListProps {
  orderId: string;
  refreshHandler?: () => void;
  clickTrackHandler?: () => void;
  commonTrackParams?: {
    orderId: string;
    orderStatus: string;
  };
}

const afsRecordList: React.FC<afsRecordListProps> = observer(({ orderId, refreshHandler, clickTrackHandler, commonTrackParams }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [recordList, setRecordList] = useState<RecordListResVo[] | []>([]);
  const [afsAgainCancel, setAfsAgainCancel] = useState(false);
  const [curServiceId, setCurServiceId] = useState("");
  const [isItemViisible, setIsItemViisible] = useState(false);

  useEffect(() => {
    loadRecordlList();
  }, []);

  // 获取售后单列表
  const loadRecordlList = async () => {
    const requestData: IRecordListReq = { orderId };
    const [err, recordListData] = await to(fetchRecordList(requestData));

    if (err) {
      setRecordList([]);
    } else {
      setRecordList(recordListData as RecordListResVo[]);
    }
  };

  if (!recordList?.length) {
    return null;
  }

  // 复制售后单号
  const copyHandler = () => {
    UKToast.show({
      content: t("order.orderDetail.common.copySuccessTips"), // 弹窗内容
      icon: "success", // 可选：success, fail, loading, warn, info，影响图标
      duration: 2000, // 不写的话默认3000
    });
  };

  // 取消售后
  const cancelRequestHandler = async () => {
    setAfsAgainCancel(false);
    const requestData: ICancelRecordReq = { afsServiceId: curServiceId };
    const [err, cancelAfsRecordData] = await to(cancelAfsRecord(requestData));

    if (err) {
      UKToast.show({
        content: t("afterSales.statusArea.cancelFailTips"), // 弹窗内容
        icon: "warning", // 可选：success, fail, loading, warn, info，影响图标
        duration: 2000, // 不写的话默认3000
      });
    } else {
      console.log(cancelAfsRecordData, "ewe");
      if (parseFloat(cancelAfsRecordData?.code) === 200 && cancelAfsRecordData?.success) {
        UKToast.show({
          content: t("afterSales.statusArea.cancelSuccessTips"), // 弹窗内容
          icon: "success", // 可选：success, fail, loading, warn, info，影响图标
          duration: 2000, // 不写的话默认3000
        });
        setTimeout(() => {
          loadRecordlList();
          refreshHandler && refreshHandler();
        }, 2000);
      } else {
        UKToast.show({
          content: t("afterSales.statusArea.cancelFailTips"), // 弹窗内容
          icon: "warning", // 可选：success, fail, loading, warn, info，影响图标
          duration: 2000, // 不写的话默认3000
        });
      }
    }
  };

  // 跳转售后详情
  const jumpToDetail = (item: RecordListResVo) => {
    // 埋点
    clickTrackHandler && clickTrackHandler();
    navigate(`/my-orders/refund?afsServiceId=${item.afsServiceId}&orderId=${item.orderId}`);
  };

  // 展开与收起功能
  const toggleExpand = () => {
    setIsItemViisible(!isItemViisible);
  };

  const recordListRender = () => {
    // 默认展示2个
    const showRecordList = !isItemViisible ? recordList.slice(0, 2) : recordList;
    return (
      <div className={styles.outer_common_layout}>
        {showRecordList.map((item: RecordListResVo, index: number) => {
          return (
            <div className={styles.common_afs_wrap} key={index}>
              <div className={styles.common_layout}>
                <div className={styles.after_sales_wrap}>
                  <i className={styles.after_sales_ico} />
                  <span className={styles.after_sales_title}>{item.afsServiceStatusTextInOrder}</span>
                </div>
                <div className={styles.btn_group}>
                  {item?.allowCancel && (
                    <CommonButton
                      variant="outlined"
                      onClick={() => {
                        setCurServiceId(item.afsServiceId);
                        setAfsAgainCancel(true);
                      }}
                      style={{
                        marginRight: "8px",
                      }}
                    >
                      Withdraw
                    </CommonButton>
                  )}
                  <CommonButton
                    variant="outlined"
                    onClick={() => {
                      jumpToDetail(item);
                    }}
                    exptag={"exp|" + EXPOSURE_RECORD.OrderDetail_AftersaleDetailExpo}
                    data-exptag-config={JSON.stringify({
                      useClick: false,
                      stay_duration: 0.3,
                      repeated: false,
                      area_rate: 0.5,
                    })}
                    data-exptag-json-param={JSON.stringify({ ...commonTrackParams, skuid: item.skuList?.map((ele) => ele.skuId)?.join(",") })}
                  >
                    {t("order.orderDetail.orderDetailStatus.refundDetailBtnText")}
                  </CommonButton>
                </div>
              </div>
              <div
                className={styles.after_sales_detail}
                dangerouslySetInnerHTML={{
                  __html: item.afsServiceStateDesc?.replace("##amount##", `<span class=${styles.orderPriceStyle}>${item.afsServiceRefundAmount}</span>`) || "",
                }}
              ></div>
              <div className={styles.base_info_item}>
                <div>Refund ID</div>
                <div>
                  {item.afsServiceId}
                  <CopyToClipboard text={item.afsServiceId} onCopy={() => copyHandler()}>
                    <span className={styles.copy_btn}>{t("order.orderDetail.common.copyText")}</span>
                  </CopyToClipboard>
                </div>
              </div>
              <div className={styles.base_info_item}>
                <div>Request time</div>
                <div>{item.applyDateShow}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className={styles.record_list_wrap}>
        {/* 一个申请单 */}
        {recordList?.length === 1 && (
          <div className={cn(styles.detail_desc_wrap, styles.detail_desc_wrap_separate)}>
            <div className={styles.after_sales_wrap}>
              <i className={styles.after_sales_ico} />
              <span className={styles.after_sales_title}>{recordList[0]?.afsServiceStatusTextInOrder}</span>
              <span
                className={styles.after_sales_detail}
                dangerouslySetInnerHTML={{
                  __html:
                    recordList[0]?.afsServiceStateDesc?.replace(
                      "##amount##",
                      `<span class=${styles.orderPriceStyle}>${recordList[0].afsServiceRefundAmount}</span>`,
                    ) || "",
                }}
              ></span>
            </div>
            <div className={styles.btn_group}>
              {recordList[0]?.allowCancel && (
                <CommonButton
                  variant="outlined"
                  onClick={() => {
                    setCurServiceId(recordList[0]?.afsServiceId);
                    setAfsAgainCancel(true);
                  }}
                  style={{
                    marginRight: "8px",
                  }}
                >
                  Withdraw
                </CommonButton>
              )}
              <CommonButton
                variant="outlined"
                onClick={() => {
                  jumpToDetail(recordList[0]);
                }}
              >
                {t("order.orderDetail.orderDetailStatus.refundDetailBtnText")}
              </CommonButton>
            </div>
          </div>
        )}
        {/* 两个申请单 */}
        {recordList?.length >= 2 && recordListRender()}

        {/* 展开与收起功能，只有超过两个才展示 */}
        {recordList?.length > 2 ? (
          <div className={styles.view_more} onClick={toggleExpand}>
            {isItemViisible ? t("order.orderList.orderItems.retractText") : t("order.orderList.orderItems.viewMoreText")}
            <VectorDown
              className={cn(styles.more_ico, {
                [styles.retract_ico]: isItemViisible,
              })}
            />
          </div>
        ) : null}
      </div>
      {/* 取消售后二次确认弹窗 */}
      <Dialog open={afsAgainCancel} onClose={() => setAfsAgainCancel(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText>{t("afterSales.statusArea.cancelModal.cancelConfirmText")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <CommonButton variant="outlined" onClick={() => setAfsAgainCancel(false)}>
            {t("afterSales.statusArea.cancelModal.cancelBtnText")}
          </CommonButton>
          <CommonButton variant="contained" onClick={cancelRequestHandler}>
            {t("afterSales.statusArea.cancelModal.confirmBtnText")}
          </CommonButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default afsRecordList;
